import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, LogOut, Settings, ShoppingBag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';
import RecentOrders from './RecentOrders';
import MobileMenu from './MobileMenu';

const Header: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isProvider, setIsProvider] = useState(false);
  const [checkingProvider, setCheckingProvider] = useState(true);

  // Check if we're on login or register pages
  const isAuthPage = location.pathname === '/login' || 
                     location.pathname === '/register' || 
                     location.pathname === '/admin-login' ||
                     location.pathname === '/admin-register';

  // Check if the logged-in user is a provider
  useEffect(() => {
    const checkIfProvider = async () => {
      if (!user) {
        setIsProvider(false);
        setCheckingProvider(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('providers')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error checking provider status:', error);
          setIsProvider(false);
        } else {
          setIsProvider(!!data);
        }
      } catch (error) {
        console.error('Error checking provider status:', error);
        setIsProvider(false);
      } finally {
        setCheckingProvider(false);
      }
    };

    checkIfProvider();
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setShowUserMenu(false);
  };

  return (
    <>
      <header className="bg-white shadow-professional-lg sticky top-0 z-40 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="relative">
                <img
                  src="/images/casap192.png"
                  alt="CasaPresta"
                  className="w-12 h-12 rounded-xl object-cover shadow-gold transform group-hover:scale-105 transition-all duration-300"
                />
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary-400 rounded-full animate-pulse"></div>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-2xl font-script font-bold text-gradient-gold">
                  CasaPresta
                </h1>
                <p className="text-xs text-gray-500 font-medium tracking-wide">
                  Services à domicile
                </p>
              </div>
            </Link>

            {/* Navigation Desktop */}
            <nav className="hidden lg:flex items-center space-x-8">
              <Link 
                to="/" 
                className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200 relative group"
              >
                Accueil
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-500 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <a 
                href="#services" 
                className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200 relative group"
              >
                Services
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-500 transition-all duration-300 group-hover:w-full"></span>
              </a>
              <Link 
                to="/about" 
                className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200 relative group"
              >
                À propos
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-500 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link 
                to="/faq" 
                className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200 relative group"
              >
                FAQ
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-500 transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </nav>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              {/* Recent Orders */}
              <div className="hidden sm:block">
                <RecentOrders />
              </div>

              {/* Provider Menu - Only show if user is a provider */}
              {user && isProvider && !checkingProvider ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-3 p-2 rounded-xl hover:bg-gray-50 transition-colors duration-200 group"
                  >
                    <div className="w-10 h-10 gradient-gold rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-300">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div className="hidden sm:block text-left">
                      <p className="text-sm font-semibold text-gray-900">
                        {user.user_metadata?.first_name || 'Utilisateur'}
                      </p>
                      <p className="text-xs text-gray-500">Prestataire</p>
                    </div>
                  </button>

                  {/* User Dropdown */}
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-professional-lg border border-gray-100 py-2 z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">
                          {user.user_metadata?.first_name} {user.user_metadata?.last_name}
                        </p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                      
                      <Link
                        to="/provider-dashboard"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200"
                      >
                        <Settings className="h-4 w-4 mr-3" />
                        Dashboard
                      </Link>
                      
                      <button
                        onClick={handleSignOut}
                        className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                      >
                        <LogOut className="h-4 w-4 mr-3" />
                        Déconnexion
                      </button>
                    </div>
                  )}
                </div>
              ) : !isAuthPage ? (
                <>
                  {/* Connexion button - Visible on mobile and desktop - Hidden on auth pages */}
                  <Link
                    to="/login"
                    className="px-4 py-2 text-sm sm:text-base text-gray-700 font-medium hover:text-primary-600 transition-colors duration-200"
                  >
                    Connexion
                  </Link>
                  
                  {/* Devenir prestataire - Desktop only (lg and up) - Hidden on mobile and auth pages */}
                  <Link
                    to="/register"
                    className="hidden lg:inline-flex btn-primary text-sm"
                  >
                    Devenir prestataire
                  </Link>
                </>
              ) : null}

              {/* Client Profile & Orders - Desktop only */}
              <div className="hidden lg:flex items-center space-x-2">
                <Link
                  to="/client-profile"
                  className="p-2.5 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200"
                  title="Mon profil"
                >
                  <User className="h-5 w-5" />
                </Link>
                <Link
                  to="/client/orders"
                  className="p-2.5 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200"
                  title="Mes commandes"
                >
                  <ShoppingBag className="h-5 w-5" />
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <div className="lg:hidden">
                <MobileMenu />
              </div>
            </div>
          </div>
        </div>

        {/* Click outside to close user menu */}
        {showUserMenu && (
          <div 
            className="fixed inset-0 z-30" 
            onClick={() => setShowUserMenu(false)}
          />
        )}
      </header>
    </>
  );
};

export default Header;